import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listOrganizationsByType,
  getServiceCoverageMatrix,
  setServiceCoverage,
} from '../../api/organizations.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

/**
 * Per-org service coverage config (value/billing Phase 2). ICER platform
 * admin only. For one organisation, classify each conditional service
 * (lab / medication / radiology / ambulance) as:
 *
 *   Covered  — included in the subscription (counts as value delivered)
 *   Billable — org pays on top (adds to amount owed on the statement)
 *   Off      — not covered (omitted from the statement; patient pays OOP)
 *
 * The always-covered set (consultations, exams, bedside, reports) is not
 * shown — it is covered for every org by design. Persists to
 * ServiceRate.coverage_mode via the admin-only set_coverage endpoint.
 */

const MODES = [
  {
    value: 'covered_in_subscription',
    label: 'Covered',
    on: 'bg-emerald-600 text-white border-emerald-600',
    off: 'text-emerald-700 border-emerald-200 hover:bg-emerald-50',
    hint: 'Included in the subscription — counts as value delivered, not separately billed.',
  },
  {
    value: 'billable_addon',
    label: 'Billable',
    on: 'bg-sky-600 text-white border-sky-600',
    off: 'text-sky-700 border-sky-200 hover:bg-sky-50',
    hint: 'Org pays on top of the subscription — adds to the amount owed.',
  },
  {
    value: 'excluded',
    label: 'Off',
    on: 'bg-slate-600 text-white border-slate-600',
    off: 'text-slate-600 border-slate-200 hover:bg-slate-50',
    hint: 'Not covered — omitted from the statement; patient pays out of pocket.',
  },
]

export default function ServiceCoverageConfigPage() {
  const qc = useQueryClient()
  const [orgId, setOrgId] = useState(null)

  const orgsQ = useQuery({
    queryKey: ['orgs-list'],
    queryFn: () => listOrganizationsByType(),
  })

  const matrixQ = useQuery({
    queryKey: ['service-coverage', orgId],
    queryFn: () => getServiceCoverageMatrix(orgId),
    enabled: Boolean(orgId),
  })

  const mut = useMutation({
    mutationFn: ({ serviceType, mode }) =>
      setServiceCoverage({ orgId, serviceType, mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-coverage', orgId] }),
  })

  const services = matrixQ.data?.services || []
  const pendingFor = mut.isPending ? mut.variables?.serviceType : null

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Service coverage"
          subtitle="Classify each conditional service per organisation: Covered (in the subscription), Billable (charged on top), or Off (patient pays out of pocket). Always-covered services — consultations, exams, bedside tests, reports — aren't shown here."
        />
        <div className="mt-2 max-w-md">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            Organisation
          </div>
          <select
            value={orgId || ''}
            onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : null)}
            className="w-full border border-sand-300 rounded-md px-3 py-1.5 text-sm bg-white"
          >
            <option value="">Select an organisation…</option>
            {(orgsQ.data || []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
      </Card>

      {mut.error && <ErrorBanner error={mut.error} />}
      {matrixQ.error && <ErrorBanner error={matrixQ.error} onRetry={() => matrixQ.refetch()} />}

      {orgId && matrixQ.isLoading && (
        <Card><div className="text-sm text-slate-400">Loading coverage…</div></Card>
      )}

      {orgId && matrixQ.data && (
        <Card>
          <CardHeader
            title={`Conditional services — ${matrixQ.data.organization_name}`}
            subtitle="Click a mode to set it. The first change creates a per-org rate row cloning the platform default's pricing; the price itself is unchanged."
          />
          <div className="divide-y divide-slate-100">
            {services.map((s) => (
              <ServiceRow
                key={s.service_type}
                service={s}
                busy={pendingFor === s.service_type}
                onSet={(mode) => mut.mutate({ serviceType: s.service_type, mode })}
              />
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-400">
            Markup shown is internal (ICER reconciliation) and never appears on the org-facing statement.
          </div>
        </Card>
      )}
    </div>
  )
}

function ServiceRow({ service, busy, onSet }) {
  const markup = service.markup_pct != null
    ? `${Number(service.markup_pct).toFixed(0)}% markup`
    : (service.rate_pgk != null ? `K${service.rate_pgk}` : '—')
  return (
    <div className="py-3 flex items-center justify-between gap-3">
      <div>
        <div className="font-medium text-slate-800">{service.service_type_label}</div>
        <div className="text-[11px] text-slate-500">
          {markup}
          {service.source === 'override'
            ? ' · per-org override'
            : service.source === 'default'
              ? ' · inherited default'
              : ''}
        </div>
      </div>
      <div className="flex gap-1.5">
        {MODES.map((m) => {
          const active = service.coverage_mode === m.value
          return (
            <button
              key={m.value}
              type="button"
              disabled={busy}
              title={m.hint}
              onClick={() => { if (!active) onSet(m.value) }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-50 ${active ? m.on : m.off}`}
            >
              {m.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
